/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import Card from '../../../../components/Card';
import ArrowBack from '../../../../assets/icons/arrow-back-icon.svg';
import UploadFile from './UploadFile';
import useAuthenticate from '../../../../hooks/useAuthenticate';
import { jsonApiAxios } from '../../../../services/api';
import FlightsPage from './FlightsPage';
import { useIntl } from 'react-intl';
import messages from '../messages';
import { useTranslatedNavigate } from '../../../../utils/routes';
import routes from '../../../../messages/routes';
import translations from '../../../../translations/main.json';
import AccountContainer from '../../../../components/AccountContainer';
import { useJsonApi } from '../../../../services/GlobalStore/GlobalStore';

const subscriptionTripSchema = {
  attributes: ['file', 'flights'],
  file: {
    ref: 'id',
  },
};

const subscriptionFlightsSchema = {
  attributes: [
    'subscriptionTrip',
    'flight_code',
    'flight_date',
    'departureAirport',
    'airline',
    'arrivalAirport',
    'status',
  ],
  subscriptionTrip: {
    ref: 'id',
  },
  departureAirport: {
    ref: 'id',
  },
  airline: {
    ref: 'id',
  },
  arrivalAirport: {
    ref: 'id',
  },
};

const tripSchema = {
  attributes: ['status'],
};

const UploadDocument = () => {
  const [fileId, setfileId] = useState('');
  const [isLoadingTrip, setIsLoadingTrip] = useState(false);
  const [flightsFound, setFlightsFound] = useState([]);
  const [flightsToSave, setFlightsToSave] = useState([]);
  const [fileStatus, setFileStatus] = useState('');
  const [errorReason, setErrorReason] = useState('');
  const [tripId, setTripId] = useState('');
  const [tripsArray, setTripsArray] = useState([]);
  const { token } = useAuthenticate();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { formatMessage } = useIntl();
  const translatedNavigate = useTranslatedNavigate();

  const { data: subscription, loading: subscriptionLoading } = useJsonApi(
    `/subscriptions`,
    {
      cachePolicy: 'no-cache',
      headers: { Authorization: `Bearer ${token}` },
    },
    []
  );

  if (subscription?.[0]?.stripe_status !== 'active' && !subscriptionLoading) {
    translatedNavigate(
      '/' +
        translations['cc4.seo.route.account'] +
        '/' +
        translations['cc4.seo.route.my-premium']
    );
  }

  const handleBackToUpload = () => {
    setTripId('');
    setfileId('');
    setIsLoadingTrip(false);
  };

  const handleBack = () => {
    translatedNavigate(
      '/' +
        translations['cc4.seo.route.account'] +
        '/' +
        translations['cc4.seo.route.my-premium']
    );
  };

  const handleContinue = async () => {
    if (fileId) {
      setIsLoadingTrip(true);
      const trip = await jsonApiAxios.post(
        '/subscriptionTrips',
        {
          file: {
            id: fileId,
          },
        },
        {
          schema: subscriptionTripSchema,
          type: 'subscriptionTrips',
          headers,
        }
      );

      if (trip?.length === 1 && trip[0]?.status_reason?.length) {
        setErrorReason(trip[0]?.status_reason);
        setIsLoadingTrip(false);
        if (
          trip[0]?.status_reason !== 'flights_not_at_least_48_hours_in_future'
        ) {
          return;
        }
      }
      if (trip?.length) {
        setTripId(trip[0]?.id);
        setTripsArray(trip.map(item => item.id));
        setFileStatus(trip[0]?.file_status);
        setIsLoadingTrip(false);
      }
    }
  };

  const fetchTrip = async () => {
    if (fileId && tripId) {
      await jsonApiAxios.get(`/subscriptionTrips/${tripId}`, {
        headers,
      });
    }
  };

  useEffect(() => {
    if (fileId) {
      handleContinue();
    }
  }, [fileId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (fileStatus !== 'Finished') {
        fetchTrip().then(res => {
          if (res?.errors?.[0]?.status === '404') {
            clearInterval(interval);
          }
        });
      }
    }, 3000);

    if (fileStatus === 'Finished') {
      setIsLoadingTrip(false);
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [fileStatus]);

  const addFlightToTrip = async () => {
    if (tripId) {
      const flightValues: any[] = [];
      flightsFound?.forEach(flight => {
        flight?.all_subscription_flights?.forEach(item => {
          flightsToSave?.forEach(itemSave => {
            if (item?.id === itemSave.toString()) {
              flightValues.push({
                id: item?.id,
                status: 'Approved',
              });
            }
          });
        });
      });

      await updateFlights(flightValues);
      uppdateTrip();
    }
  };

  const updateFlights = async (values: any) => {
    const payload: { 'atomic:operations': any[] } = {
      'atomic:operations': [],
    };
    values?.forEach(value => {
      payload['atomic:operations'].push({
        op: 'update',
        href: `/subscriptionFlights/${value?.id}`,
        data: {
          type: 'subscriptionFlights',
          id: value?.id,
          attributes: {
            status: 'Approved',
          },
        },
      });
    });
    return jsonApiAxios.post('/operations', payload, {
      noSerialize: true,
    });
  };

  const uppdateTrip = async () => {
    const promises = tripsArray.map(itemId =>
      jsonApiAxios.patch(
        `/subscriptionTrips/${itemId}`,
        {
          status: 'Active',
        },
        {
          schema: tripSchema,
          type: 'subscriptionTrips',
          headers,
        }
      )
    );

    return Promise.all(promises).then(() => {
      translatedNavigate(
        '/' +
          translations['cc4.seo.route.account'] +
          '/' +
          translations['cc4.seo.route.my-premium']
      );
    });
  };

  const moveToManual = () => {
    translatedNavigate(routes.enterManual);
  };

  return (
    <>
      {subscription?.[0]?.stripe_status === 'active' ? (
        <AccountContainer>
          <Card style={cardStyles}>
            {tripId && !isLoadingTrip ? (
              <div className="select-trip-container">
                <div className="select-trip-header">
                  <div
                    className="select-trip-back"
                    onClick={handleBackToUpload}
                  >
                    <img
                      src={ArrowBack}
                      alt=""
                      width={6}
                      height={12}
                      style={{ marginLeft: '4px' }}
                    />
                    <span>Back</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="select-trip-main-header">
                      {formatMessage(messages.flightsFoundLabel)}
                    </span>
                    <span className="select-trip-main-subheader">
                      {formatMessage(messages.manualEntrySubTitle)}
                    </span>
                  </div>
                </div>
                <FlightsPage
                  tripId={tripId}
                  tripsArray={tripsArray}
                  setFlightsToSave={setFlightsToSave}
                  setFlightsFound={setFlightsFound}
                  errorReason={errorReason}
                />
                <div className="sub-flights-found-text">
                  <span style={{ color: '#A8A8AA' }}>
                    {formatMessage(messages.wrongLabel)}
                  </span>{' '}
                  <span
                    style={{
                      color: '#279C74',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                    onClick={moveToManual}
                  >
                    {formatMessage(messages.enterManuallyLable)}
                  </span>
                </div>
                <div className="footer-button">
                  <button
                    onClick={addFlightToTrip}
                    disabled={!flightsToSave?.length}
                  >
                    {formatMessage(messages.saveTripsLabel)}
                  </button>
                </div>
              </div>
            ) : (
              <div className="select-trip-container">
                <div className="select-trip-header">
                  <div className="select-trip-back" onClick={handleBack}>
                    <img
                      src={ArrowBack}
                      alt=""
                      width={6}
                      height={12}
                      style={{ marginLeft: '4px' }}
                    />
                    <span>Back</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="select-trip-main-header">
                      {formatMessage(messages.protectNewTripLabel)}
                    </span>
                    <span className="select-trip-main-subheader">
                      {formatMessage(messages.manualEntrySubTitle)}
                    </span>
                  </div>
                </div>
                <UploadFile
                  setfileId={setfileId}
                  isLoadingTrip={isLoadingTrip}
                  fileId={fileId}
                  setErrorReason={setErrorReason}
                  errorReason={
                    errorReason === 'flights_not_at_least_48_hours_in_future'
                      ? null
                      : errorReason
                  }
                />
                {errorReason !== 'no_flights' && (
                  <div className="footer-button">
                    <button
                      onClick={handleContinue}
                      disabled={!fileId || isLoadingTrip}
                    >
                      {formatMessage(messages.continueButton)}
                    </button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </AccountContainer>
      ) : null}
    </>
  );
};

export default UploadDocument;

const cardStyles = {
  borderRadius: '12px',
  boxShadow: '0px 4px 8px 0px #D8D8DA',
};
